import * as React from "react";
import { Alert, Box, Chip, Collapse, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { CaretDownIcon, CaretRightIcon, TrashIcon } from "@phosphor-icons/react/dist/ssr";

import { ScheduleGrid } from "./schedule-grid";
import { hasOverlap, mergeAdjacentSlots, TimeSlot, validateTimeSlot } from "./types";

const formatTimeSlot = (slot: TimeSlot): string => {
	return `${slot.startTime} - ${slot.endTime}`;
};

interface SchedulePickerProps {
	value?: TimeSlot[];
	onChange?: (slots: TimeSlot[]) => void;
	disabled?: boolean;
	showSummary?: boolean;
	maxSlotsPerDay?: number;
}

const DAYS = [
	{ id: 1, name: "Mon", fullName: "Monday" },
	{ id: 2, name: "Tue", fullName: "Tuesday" },
	{ id: 3, name: "Wed", fullName: "Wednesday" },
	{ id: 4, name: "Thu", fullName: "Thursday" },
	{ id: 5, name: "Fri", fullName: "Friday" },
	{ id: 6, name: "Sat", fullName: "Saturday" },
	{ id: 7, name: "Sun", fullName: "Sunday" },
];

export function SchedulePicker({
	value = [],
	onChange,
	disabled = false,
	showSummary = true,
	maxSlotsPerDay = 24,
}: SchedulePickerProps) {
	const [errors, setErrors] = React.useState<string[]>([]);
	const [isSummaryExpanded, setIsSummaryExpanded] = React.useState(false);

	const mergedSlots = React.useMemo(() => mergeAdjacentSlots(value), [value]);

	const validateSlots = React.useCallback(
		(slots: TimeSlot[]) => {
			const newErrors: string[] = [];

			// Check for invalid slots
			for (const [index, slot] of slots.entries()) {
				if (!validateTimeSlot(slot)) {
					newErrors.push(`Slot ${index + 1}: invalid time`);
				}
			}

			// Check for overlaps
			const slotsByDay = slots.reduce(
				(acc, slot) => {
					if (!acc[slot.day]) acc[slot.day] = [];
					acc[slot.day].push(slot);
					return acc;
				},
				{} as Record<number, TimeSlot[]>
			);

			for (const [day, daySlots] of Object.entries(slotsByDay)) {
				if (daySlots.length > maxSlotsPerDay) {
					newErrors.push(`${DAYS[Number.parseInt(day) - 1]?.fullName}: maximum ${maxSlotsPerDay} slots per day`);
				}

				// Check overlaps within the same day
				for (let i = 0; i < daySlots.length; i++) {
					for (let j = i + 1; j < daySlots.length; j++) {
						if (hasOverlap([daySlots[i]], daySlots[j])) {
							newErrors.push(`${DAYS[Number.parseInt(day) - 1]?.fullName}: overlap of time slots`);
							break;
						}
					}
				}
			}

			setErrors(newErrors);
			return newErrors.length === 0;
		},
		[maxSlotsPerDay]
	);

	const handleChange = React.useCallback(
		(newSlots: TimeSlot[]) => {
			const isValid = validateSlots(newSlots);
			if (isValid && onChange) {
				onChange(newSlots);
			}
		},
		[onChange, validateSlots]
	);

	const handleClear = () => {
		if (onChange) {
			onChange([]);
			setErrors([]);
		}
	};

	const getSlotsByDay = (day: number) => {
		return mergedSlots.filter((slot) => slot.day === day);
	};

	return (
		<Box>
			{/* Summary */}
			{showSummary && (
				<Box>
					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						justifyContent="space-between"
						onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
						sx={{ cursor: "pointer" }}
					>
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography variant="subtitle2">Schedule:</Typography>
							<Chip label={`${mergedSlots.length} slots`} size="small" color="primary" variant="outlined" />
							{!disabled && mergedSlots.length > 0 && (
								<Tooltip title="Clear all slots">
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleClear();
										}}
									>
										<TrashIcon />
									</IconButton>
								</Tooltip>
							)}
						</Stack>

						<Tooltip title={isSummaryExpanded ? "Collapse" : "Expand"}>
							<IconButton size="small" onClick={() => setIsSummaryExpanded(!isSummaryExpanded)} sx={{ p: 0.5 }}>
								{isSummaryExpanded ? <CaretDownIcon size={16} /> : <CaretRightIcon size={16} />}
							</IconButton>
						</Tooltip>
					</Stack>

					{/* Day summaries */}
					<Collapse in={isSummaryExpanded} timeout={600}>
						<Stack spacing={1} mt={1}>
							{DAYS.map((day) => {
								const daySlots = getSlotsByDay(day.id);
								return (
									<Box key={day.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography variant="body2" sx={{ minWidth: "60px", minHeight: "24px", fontWeight: 500 }}>
											{day.name}:
										</Typography>
										{daySlots.length === 0 ? (
											<Typography variant="body2" color="text.secondary">
												-
											</Typography>
										) : (
											<Stack direction="row" spacing={0.5} flexWrap="wrap">
												{daySlots.map((slot, index) => (
													<Chip
														key={index}
														label={formatTimeSlot(slot)}
														size="small"
														variant="outlined"
														color="primary"
													/>
												))}
											</Stack>
										)}
									</Box>
								);
							})}

							{/* Errors */}
							{errors.length > 0 && (
								<Alert severity="error">
									<Typography variant="subtitle2" sx={{ mb: 1 }}>
										Validation errors:
									</Typography>
									<ul style={{ margin: 0, paddingLeft: "20px" }}>
										{errors.map((error, index) => (
											<li key={index}>
												<Typography variant="body2">{error}</Typography>
											</li>
										))}
									</ul>
								</Alert>
							)}

							{/* Schedule Grid */}
							<ScheduleGrid value={value} onChange={handleChange} disabled={disabled} />

							{/* Instructions */}
							{!disabled && (
								<Box>
									<Typography variant="caption" color="text.secondary">
										💡 Click and drag on the grid to add a time slot. Click on an existing slot to delete it.
									</Typography>
								</Box>
							)}
						</Stack>
					</Collapse>
				</Box>
			)}
		</Box>
	);
}
